import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, CarFront, Save, EyeOff, Eye } from "lucide-react";
import { API, fileSrc, invalidateCategoryCache } from "@/lib/api";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

function CategoryEditor({ cat, meta, auth, onChange }) {
  const [form, setForm] = useState({ ...cat, features: (cat.features || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/categories/${cat.id}`, {
        ...form,
        order: Number(form.order),
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      }, auth);
      invalidateCategoryCache();
      toast.success("კატეგორია შენახულია");
      onChange();
    } catch {
      toast.error("შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/categories/${cat.id}/photo`, fd, auth);
      invalidateCategoryCache();
      toast.success("კატეგორიის ფოტო განახლდა");
      onChange();
    } catch (e2) {
      toast.error(typeof e2.response?.data?.detail === "string" ? e2.response.data.detail : "ატვირთვა ვერ მოხერხდა");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const cover = meta?.cover_url ? fileSrc(meta.cover_url) : null;

  return (
    <form onSubmit={save} className="card-lux p-6" data-testid={`admin-category-${cat.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <div className="h-40 rounded-xl overflow-hidden bg-[#0D0F14] mb-3 relative">
            {cover ? (
              <img src={cover} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-600">
                <CarFront className="w-8 h-8" />
                <span className="text-xs">ფოტო არ არის</span>
              </div>
            )}
            <span className="absolute bottom-2 right-2 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#0A0B0E]/85 border border-white/20 text-white">
              {meta?.vehicle_count ?? 0} ავტომობილი · {meta?.available_count ?? 0} თავისუფალი
            </span>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`admin-category-upload-${cat.id}`}
            className="btn-ghost !py-2 !px-4 text-xs w-full"
          >
            <UploadCloud className="w-3.5 h-3.5" /> {uploading ? "იტვირთება..." : "წარმომადგენელი ფოტოს შეცვლა"}
          </button>
          <input type="file" accept="image/*" hidden ref={inputRef} data-testid={`admin-category-input-${cat.id}`} onChange={(e) => uploadPhoto(e.target.files[0])} />
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">სათაური</label>
            <input data-testid={`admin-category-name-${cat.id}`} value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">წლები</label>
            <input data-testid={`admin-category-years-${cat.id}`} value={form.years} onChange={set("years")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">ფასი / დიაპაზონი</label>
            <input data-testid={`admin-category-price-${cat.id}`} value={form.priceLabel} onChange={set("priceLabel")} className={inputCls} placeholder="80–85 ₾" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Sale ფასი / დიაპაზონი</label>
            <input data-testid={`admin-category-sale-price-${cat.id}`} value={form.sale_price_label || ""} onChange={set("sale_price_label")} className={inputCls} placeholder="70–75 ₾" />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid={`admin-category-sale-toggle-${cat.id}`} checked={!!form.sale_enabled} onChange={set("sale_enabled")} className="w-4 h-4 accent-[#D4AF37]" />
              ფასდაკლება აქტიურია
            </label>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">დეპოზიტი</label>
            <input data-testid={`admin-category-deposit-${cat.id}`} value={form.depositLabel} onChange={set("depositLabel")} className={inputCls} placeholder="200 ₾" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">ბეიჯი</label>
            <input data-testid={`admin-category-badge-${cat.id}`} value={form.badge} onChange={set("badge")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">რიგითობა</label>
            <input data-testid={`admin-category-order-${cat.id}`} type="number" value={form.order} onChange={set("order")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">მოდელის ჯგუფი</label>
            <select data-testid={`admin-category-model-${cat.id}`} value={form.model} onChange={set("model")} className={inputCls}>
              <option value="prius" className="bg-[#141721]">Toyota Prius</option>
              <option value="tesla" className="bg-[#141721]">Tesla</option>
              <option value="byd" className="bg-[#141721]">BYD</option>
            </select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid={`admin-category-hidden-${cat.id}`} checked={!!form.hidden} onChange={set("hidden")} className="w-4 h-4 accent-[#D4AF37]" />
              {form.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} კატეგორიის დამალვა საიტიდან
            </label>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-400 mb-1.5">მოკლე აღწერა</label>
            <input data-testid={`admin-category-tagline-${cat.id}`} value={form.tagline} onChange={set("tagline")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-400 mb-1.5">ქირის პირობები (თითო ხაზზე თითო)</label>
            <textarea data-testid={`admin-category-features-${cat.id}`} rows={4} value={form.features} onChange={set("features")} className={`${inputCls} resize-none`} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-400 mb-1.5">შენიშვნა (ოქროს ბლოკი)</label>
            <textarea data-testid={`admin-category-note-${cat.id}`} rows={2} value={form.note} onChange={set("note")} className={`${inputCls} resize-none`} />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={saving} data-testid={`admin-category-save-${cat.id}`} className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "ინახება..." : "შენახვა"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default function AdminCategories({ auth }) {
  const [cats, setCats] = useState([]);
  const [meta, setMeta] = useState({});

  const load = () => {
    axios.get(`${API}/admin/categories`, auth).then((r) => setCats(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => {
      const map = {};
      r.data.forEach((c) => (map[c.id] = c));
      setMeta(map);
    });
  };

  useEffect(load, []);

  return (
    <div data-testid="admin-categories-section">
      <p className="text-sm text-gray-400 mb-6">
        კატეგორიების სრული მართვა: სათაური, წლები, ფასი, დეპოზიტი, პირობები, რიგითობა, დამალვა და წარმომადგენელი ფოტო. ავტომობილების რაოდენობა ითვლება ავტომატურად.
      </p>
      <div className="space-y-6">
        {cats.map((c) => (
          <CategoryEditor key={c.id} cat={c} meta={meta[c.id]} auth={auth} onChange={load} />
        ))}
      </div>
    </div>
  );
}
